<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { computed, ref } from "vue";
import { MaskClose } from "@nutui/icons-vue";
import { useFormModel, useImage, useUploadImage } from "@/hooks";
import { errorLog, error, getToken, warning, compressImage } from "@/utils";

const props = defineProps<{
  height?: string;
  width?: string;
  maximize: number;
  src?: string;
  modelValue?: string;
  // validateEvent: boolean;
}>();

const emit = defineEmits(["update:modelValue", "change"]);

const uploadRef = ref();
const headers = { token: getToken() };

const { imageUrl } = useUploadImage(computed(() => props.modelValue));
const { imageUrl: bgUrl } = useImage(computed(() => props.src));

const apiUpload = computed(
  () => `/api/File/Upload`
);

const styleObj = computed(() => ({
  width: props.width,
  height: props.height,
}));

const uploadMaximize = computed(() => props.maximize * 1024);

function onDelete() {
  emit("update:modelValue", "");
  emit("change", "");
  // if (props.validateEvent) {
  //   elFormItem?.validate?.("change").catch((err) => console.log(err));
  // }
}

function onSuccess({ responseText }: { responseText: string }) {
  uploadRef.value.clearUploadQueue();
  const result = JSON.parse(responseText);
  if (!result.success) return;
  emit("update:modelValue", result.data[0].fileName);
  emit("change", result.data[0].fileName);
}

function onFailure({ responseText }: { responseText: string }) {
  errorLog(responseText);
  error("图片上传出错了，请联系管理员");
}

function onOversize() {
  warning(`文件大小不能超过${props.maximize}KB`);
}

const beforeUpload = async (files: any[]) => {
  const file = await compressImage(files[0], 0.5);
  return [file];
};

const { setFormModel } = useFormModel();
defineExpose({ setFormModel });
</script>
<template>
  <div class="mp-image-uploader" :style="styleObj">
    <div class="mp-image-uploader__container" v-if="imageUrl">
      <PreviewableImage
        class="mp-image-uploader__img"
        :src="imageUrl"
        :width="width"
        :height="height"
        v-bind="$attrs"
      />
      <div class="mp-image-uploader__actions">
        <span class="mp-image-uploader__delete" @click.stop="onDelete">
          <MaskClose />
        </span>
      </div>
    </div>
    <nut-uploader
      class="mp-image-uploader__empty"
      :url="apiUpload"
      :headers="headers"
      :auto-upload="true"
      :multiple="false"
      :maximize="uploadMaximize"
      :before-upload="beforeUpload"
      ref="uploadRef"
      @success="onSuccess"
      @failure="onFailure"
      @oversize="onOversize"
      v-else
    >
      <template #default v-if="bgUrl">
        <nut-image
          class="mp-image-uploader__img"
          :src="bgUrl"
          :width="width"
          :height="height"
        />
      </template>
    </nut-uploader>
  </div>
</template>
<style lang="scss">
.el-form-item.is-error .mp-image-uploader {
  box-shadow: 0 0 0 1px #f56c6c;
}

.mp-image-uploader {
  &__container {
    position: relative;
  }

  &__container,
  &__img,
  &__actions,
  &__empty {
    width: 100%;
    height: 100%;
  }

  &__actions {
    position: absolute;
    left: 0;
    top: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    font-size: 20px;
  }
}
</style>
